// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import SwiperCore, { A11y, Controller, Navigation, Pagination, Scrollbar } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperClass from "swiper/types/swiper-class";
/* eslint-enable spire/fenced-imports */

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, {
    ButtonIcon,
    ButtonIconPresentationProps,
    ButtonPresentationProps,
    ButtonProps,
} from "@insite/mobius/Button";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import { swiperStyle } from "@rugby/App/Components/RugbyCarousel/RugbyCarousel.style";
import ChevronLeft from "@rugby/XDL/Icons/ChevronLeft";
import ChevronRight from "@rugby/XDL/Icons/ChevronRight";
import { MediaQuery } from "@rugby/XDL/Utilities";
import pickMultiple from "@rugby/XDL/Utilities/pickMultiple";
import React, { FC, isValidElement, useEffect, useState } from "react";
import styled, { css } from "styled-components";

SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Controller]);

export const CarouselWrapper = styled(StyledWrapper)<{
    navigationOutSide?: boolean;
    mainContainerStyle?: InjectableCss;
    swiperSlideStyle?: InjectableCss;
    centerContent?: boolean;
}>`
    ${(props: any) => props.navigationOutSide && `position: relative;`}
    width: 100%;
    max-width: 100%;
    ${(props: any) => {
        return css`
            ${props.mainContainerStyle?.css}
        `;
    }}

    ${MediaQuery.lt("xl")} {
        max-width: calc(100% - 90px);
        margin: 0 auto;
    }

    .swiper-wrapper {
        ${({ centerContent }: any) => centerContent && `justify-content: center;`}
    }

    .swiper-slide {
        &&& {
            ${(props: any) => props.swiperSlideStyle?.css}
        }
    }
    ${injectCss}
`;

export interface SwiperStyle {
    mainContainer?: InjectableCss;
    swiperSlideStyle?: InjectableCss;
    prevArrowSwiper?: ButtonPresentationProps;
    nextArrowSwiper?: ButtonPresentationProps;
    prevArrowSwiperIcon?: ButtonIconPresentationProps;
    nextArrowSwiperIcon?: ButtonIconPresentationProps;
}

export type CarouselProps = Swiper & {
    extendedStyles?: SwiperStyle;
    navigationOutSide?: boolean;
    customController?: SwiperClass[];
};

const ArrowNavigation: FC<
    ButtonProps & {
        position: "next" | "prev";
        shouldHideArrow?: boolean;
        extendedStyles?: any;
        buttonIconProps?: ButtonIconPresentationProps;
    }
> = ({ position, shouldHideArrow, extendedStyles, buttonIconProps, ...otherProps }) => {
    const buttonStyle = position === "next" ? extendedStyles.nextArrowSwiper : extendedStyles.prevArrowSwiper;
    const iconStyle = position === "next" ? extendedStyles.nextArrowSwiperIcon : extendedStyles.prevArrowSwiperIcon;
    const iconSrc = position === "next" ? ChevronRight : ChevronLeft;

    if (shouldHideArrow) {
        return null;
    }

    return (
        <Button {...buttonStyle} {...otherProps} data-test-selector={`carousel-${position}-button`}>
            <ButtonIcon
                src={iconSrc}
                {...iconStyle}
                className={`carousel-${position}-icon ${otherProps.disabled && "disabled"}`}
                {...buttonIconProps}
            />
        </Button>
    );
};

const RugbyCarousel: FC<CarouselProps> = ({
    children,
    navigationOutSide,
    extendedStyles,
    onSwiper: onSwiperProps,
    onSlideChange: onSlideChangeProps,
    customController,
    ...otherProps
}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [swiperInstance, setSwiperInstance] = useState<SwiperClass | null>(null);
    const [arrowClass, setArrowClass] = useState<{ next: string; prev: string }>({ next: "", prev: "disabled" });
    const style = mergeToNew(swiperStyle, extendedStyles);

    const [shouldHideArrow, setShouldHideArrow] = useState(true);
    const [centerContent, setCenterContent] = useState(false);

    useEffect(() => {
        const getStyle = async () => {
            // eslint-disable-next-line global-require
            await require("swiper/swiper-bundle.min.css");
        };
        getStyle().then(res => {
            setIsLoaded(true);
        });
    }, []);

    const shouldHideArrowHandler = (swiper: SwiperClass) => {
        const numOfSlides = swiper?.slides.length || 0;
        const numOfSlidesPerView = swiper?.params.slidesPerView || 0;
        setShouldHideArrow(numOfSlides <= Number(numOfSlidesPerView));
    };

    const onResizeHandler = (swiper: SwiperClass) => {
        if (!swiper) {
            return;
        }
        const centerContent = (Number(swiper.params.slidesPerView) || 0) > swiper.slides.length;
        setCenterContent(centerContent);
        shouldHideArrowHandler(swiper);
    };

    const moveSlidesForCustomController = (direction: "next" | "prev") => {
        customController?.forEach(item => {
            item.slideNext();
            if ("destroyed" in item) {
                return;
            }
            if (direction === "next") {
                (item as SwiperCore).slideNext();
            } else if (direction === "prev") {
                (item as SwiperCore).slidePrev();
            }
        });
    };

    const NextArrowSwiper = (
        <ArrowNavigation
            disabled={arrowClass.next.includes("disabled")}
            shouldHideArrow={shouldHideArrow}
            position="next"
            onClick={() => {
                swiperInstance?.slideNext();
                customController && moveSlidesForCustomController("next");
            }}
            extendedStyles={style}
        />
    );

    const PrevArrowSwiper = (
        <ArrowNavigation
            disabled={arrowClass.prev.includes("disabled")}
            shouldHideArrow={shouldHideArrow}
            position="prev"
            onClick={() => {
                swiperInstance?.slidePrev();
                customController && moveSlidesForCustomController("prev");
            }}
            extendedStyles={style}
        />
    );

    useEffect(() => {
        if (swiperInstance && swiperInstance?.slides) {
            onResizeHandler(swiperInstance);
        }
    }, [swiperInstance?.slides]);

    if (!isLoaded) {
        return null;
    }

    return (
        <CarouselWrapper
            mainContainerStyle={style.mainContainer}
            className="carousel-main-wrapper"
            navigationOutSide={navigationOutSide}
            swiperSlideStyle={style.swiperSlideStyle}
            centerContent={centerContent}
        >
            <Swiper
                watchOverflow={true}
                updateOnWindowResize={true}
                {...otherProps}
                onSwiper={(swiper: SwiperClass) => {
                    setSwiperInstance(swiper);
                    onResizeHandler(swiper);
                    onSwiperProps && onSwiperProps(swiper);
                }}
                onSlideChange={(swiper: SwiperClass) => {
                    setArrowClass({
                        next: `${swiper?.isEnd && "disabled"}`,
                        prev: `${swiper?.isBeginning && "disabled"}`,
                    });
                    onSlideChangeProps && onSlideChangeProps(swiper);
                }}
                onResize={onResizeHandler}
            >
                {React.Children.map(children, child => {
                    // for null case
                    if (!isValidElement(child)) {
                        return;
                    }

                    const { props } = child;

                    const swiperSlidePropsKeys = ["tag", "zoom", "virtualIndex"];
                    const xngKeys = ["renderWithoutSwiperSlide"];

                    const childrenProps = omitMultiple(props, [...swiperSlidePropsKeys, ...xngKeys]);
                    const swiperSlideProps = pickMultiple(props, swiperSlidePropsKeys);

                    if (props.renderWithoutSwiperSlide) {
                        return child;
                    }

                    return (
                        <SwiperSlide {...swiperSlideProps}>
                            {React.createElement(child.type, childrenProps)}
                        </SwiperSlide>
                    );
                })}

                {otherProps.navigation && (
                    <>
                        {PrevArrowSwiper}
                        {NextArrowSwiper}
                    </>
                )}

                {otherProps.pagination && <div className="swiper-pagination" />}
                {otherProps.scrollbar && <div className="swiper-scrollbar" />}
            </Swiper>

            {navigationOutSide && !otherProps.navigation && (
                <>
                    {PrevArrowSwiper}
                    {NextArrowSwiper}
                </>
            )}
        </CarouselWrapper>
    );
};

export default RugbyCarousel;
